import type { NuxtApp } from '#app';

/**
 * 路由守衛的導向出口 —— hydration 期間會先放行，等 hydration 結束再導。
 *
 * 【為什麼需要這一層】瀏覽器端的 global middleware 會在 **hydration 之前**再跑一次：
 * Nuxt 在 `app:created` 就做 `router.replace(initialRoute, { force: true })`，
 * 而 `app:created` 是在 `vueApp.mount()` 之前被 await 的
 * （見 `nuxt/dist/app/entry.js` 與 `nuxt/dist/pages/runtime/plugins/router.js`）。
 *
 * 如果這一輪判斷出來的目的地與 SSR 渲染的那一頁不同，Vue 就會拿 B 頁的 vnode 樹
 * 去 hydrate A 頁的 DOM，而且**壞得很安靜**：
 *
 *   - hydration 不會修正 class / style。Vue 的 `hydrateElement` 只 patch `value` 系列與
 *     事件監聽，其餘一律信任 SSR 的 markup —— 於是同樣是 `<div>` 的節點會就地 hydrate，
 *     B 頁的內容掛著 A 頁的版面 class，整個版面爆掉。
 *   - A 頁的元件從頭到尾沒有 mount，它 `onMounted` 才啟動的東西（例如登入頁右欄的
 *     lottie 動畫）永遠不會出現。
 *
 * 實際踩到的情境：認證 cookie 還沒遷移完、或 SSR 取組織資訊失敗時，伺服器端判「未登入」
 * 而渲染登入頁，瀏覽器端判「已登入」而要去 dashboard —— 症狀就是登入頁右邊沒有動畫、
 * 接著 dashboard 爆版（但資料是對的），重新整理又一切正常。
 *
 * 延後之後走的是正常的 SPA 導航，`<NuxtLayout>` 會照規矩換掉版面。
 * 代價是使用者會看到 SSR 那一頁一瞬間 —— 比爆版好，而且這條路徑本來就該是例外。
 *
 * @returns `true` 表示「這一輪放行、導向已排程」；否則回傳 `navigateTo` 的結果，
 *          呼叫端直接 `return` 即可。
 */
export function hydrationSafeNavigateTo(
  nuxtApp: NuxtApp,
  to: Parameters<typeof navigateTo>[0],
  options?: Parameters<typeof navigateTo>[1]
) {
  if (import.meta.client && nuxtApp.isHydrating) {
    nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
      // replace：這一次導向是在修正 SSR 的判斷，不該在瀏覽器歷史留下一筆
      Promise.resolve(
        nuxtApp.runWithContext(() => navigateTo(to, { ...options, replace: true }))
      ).catch((error) => {
        console.warn('[nav] 延後到 hydration 之後的導向失敗：', error);
      });
    });
    return true;
  }

  return navigateTo(to, options);
}
